import React, { useEffect, useState } from 'react';
import {
  Button, Form, Input, Modal, Popconfirm, Select,
  Space, Table, Tag, Typography, message,
} from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Workplace, getWorkplaces, createWorkplace, updateWorkplace, deleteWorkplace } from '../api/workplaces';
import api from '../api/client';

const { Title } = Typography;

interface Department { id: number; name: string; }

const WorkplacesPage: React.FC = () => {
  const [workplaces, setWorkplaces] = useState<Workplace[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Workplace | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadData();
    api.get<Department[] | { items?: Department[] }>('/departments')
      .then(r => {
        const data = r.data;
        setDepartments(Array.isArray(data) ? data : (data as any).items ?? []);
      });
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      setWorkplaces(await getWorkplaces());
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record: Workplace) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      department_id: record.department_id,
      description: record.description,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editing) {
        const updated = await updateWorkplace(editing.id, values);
        setWorkplaces(prev => prev.map(w => w.id === updated.id ? updated : w));
        message.success('Обновлено');
      } else {
        const created = await createWorkplace(values);
        setWorkplaces(prev => [...prev, created]);
        message.success('Создано');
      }
      setModalOpen(false);
    } catch {
      message.error('Ошибка при сохранении');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteWorkplace(id);
      setWorkplaces(prev => prev.filter(w => w.id !== id));
      message.success('Удалено');
    } catch {
      message.error('Ошибка при удалении');
    }
  };

  const columns = [
    { title: 'Название', dataIndex: 'name', key: 'name' },
    {
      title: 'Подразделение',
      key: 'dept',
      render: (_: any, r: Workplace) => r.department?.name
        ? <Tag>{r.department.name}</Tag>
        : <span style={{ color: '#ccc' }}>—</span>,
    },
    {
      title: 'Описание',
      dataIndex: 'description',
      key: 'desc',
      render: (v: any) => v ?? '—',
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_: any, record: Workplace) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm
            title="Удалить рабочее место?"
            onConfirm={() => handleDelete(record.id)}
            okText="Да"
            cancelText="Нет"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Title level={4} style={{ margin: 0 }}>Рабочие места</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Добавить</Button>
      </Space>
      <Table
        columns={columns}
        dataSource={workplaces}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        size="middle"
      />
      <Modal
        title={editing ? 'Редактировать рабочее место' : 'Новое рабочее место'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, message: 'Введите название' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="department_id" label="Подразделение">
            <Select
              allowClear
              placeholder="Не указано"
              options={departments.map(d => ({ value: d.id, label: d.name }))}
            />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WorkplacesPage;
